import { useEffect, useState } from "react";
import axios from "axios";
import { Check, Search, Users } from "lucide-react";
import { BUYER_BADGES } from "../lib/badges";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Searchable "pick your Level-2 sponsor" step shown on the Super-Stockist
 * registration flow.  Skipping this section (empty selection) makes the SS
 * an Independent 3A partner.  Picking one auto-derives the correct sub-role
 * (3B/3C/3D) on the backend based on the sponsor's category.
 */
export const SponsorPicker = ({ value, onChange, testidPrefix = "sponsor" }) => {
  const [type, setType] = useState("all"); // 'all' | 'introducer' | 'custodian' | 'guarantor'
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (type !== "all") params.set("type", type);
    if (q.trim()) params.set("q", q.trim());
    axios
      .get(`${API}/hierarchy/level2?${params}`)
      .then((r) => { if (alive) setItems(r.data || []); })
      .catch(() => { if (alive) setItems([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [type, q]);

  return (
    <div data-testid={testidPrefix + "-picker"} className="space-y-4">
      <div className="flex items-start gap-3">
        <span className="grid place-items-center w-9 h-9 rounded-full border border-[#B87333]/45 text-[#B87333] shrink-0">
          <Users size={15} />
        </span>
        <div>
          <div className="font-serif text-lg text-[#F5F5F0]">Pick your Level-2 sponsor <span className="font-body text-[#8A8A82] text-sm">(optional)</span></div>
          <p className="font-body text-xs text-[#8A8A82] mt-1 leading-relaxed">
            Skip this section to register as an <b className="text-[#F5F5F0]">Independent (3A)</b> Super-Stockist. Pick an Introducer, Custodian or Guarantor to become a <b className="text-[#F5F5F0]">3B / 3C / 3D</b> dependent partner — your orders will route through them for approval before Admin.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 rounded-full border border-white/12 p-1 bg-black/30">
          {[
            { k: "all", l: "All" },
            { k: "introducer", l: "Introducers" },
            { k: "custodian", l: "Custodians" },
            { k: "guarantor", l: "Guarantors" },
          ].map((t) => (
            <button
              key={t.k}
              type="button"
              data-testid={`${testidPrefix}-filter-${t.k}`}
              onClick={() => setType(t.k)}
              className={`px-3 py-1 rounded-full font-grotesk text-[10px] uppercase tracking-[0.16em] transition-colors ${
                type === t.k ? "bg-[#B87333] text-black" : "text-[#B8B8B0] hover:text-[#F5F5F0]"
              }`}
            >
              {t.l}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 border border-white/12 rounded-full px-3 py-1.5 flex-1 min-w-[220px] max-w-[360px]">
          <Search size={13} className="text-[#8A8A82]" />
          <input
            data-testid={`${testidPrefix}-search`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by business name or vendor code"
            className="bg-transparent outline-none font-body text-sm text-[#F5F5F0] flex-1"
          />
        </div>
      </div>

      <div className="max-h-72 overflow-auto border border-white/[0.08] rounded-sm">
        {loading && <div className="p-6 font-body text-xs text-[#8A8A82]">Searching…</div>}
        {!loading && items.length === 0 && (
          <div className="p-6 font-body text-xs text-[#8A8A82]">
            No active sponsors found. Ask your sponsor to register + get admin-approved first, then come back.
          </div>
        )}
        {!loading && items.map((s) => {
          const b = BUYER_BADGES[s.category];
          const selected = value?.id === s.id;
          return (
            <button
              type="button"
              key={s.id}
              data-testid={`${testidPrefix}-option-${s.id}`}
              onClick={() => onChange(selected ? null : s)}
              className={`w-full text-left px-4 py-3 flex items-center gap-3 border-b border-white/[0.05] last:border-b-0 transition-colors ${
                selected ? "bg-[#B87333]/[0.10]" : "hover:bg-white/[0.03]"
              }`}
            >
              <span
                className="grid place-items-center w-8 h-8 rounded-sm shrink-0 text-black"
                style={{ background: `linear-gradient(135deg, ${b?.from || "#333"}, ${b?.to || "#666"})` }}
              >
                {b?.Icon && <b.Icon size={14} />}
              </span>
              <span className="min-w-0 flex-1">
                <div className="font-serif text-base text-[#F5F5F0] truncate">{s.business_name}</div>
                <div className="font-grotesk text-[9px] uppercase tracking-[0.2em] text-[#8A8A82] mt-0.5">
                  {b?.label || s.category} · {s.state || "—"} · <span className="text-[#B87333]">{s.vendor_code}</span>
                </div>
              </span>
              {selected && (
                <span className="grid place-items-center w-6 h-6 rounded-full bg-[#B87333] text-black shrink-0">
                  <Check size={13} />
                </span>
              )}
            </button>
          );
        })}
      </div>

      {value && (
        <div data-testid={`${testidPrefix}-selected`} className="mt-2 font-body text-xs text-[#F5F5F0]">
          You'll register as <b className="text-[#B87333]">{BUYER_BADGES[value.category]?.label}-Dependent (3{value.category === "introducer" ? "B" : value.category === "guarantor" ? "C" : "D"})</b>. Your orders will queue in <b>{value.business_name}</b>'s approval inbox before reaching Vinci Admin.
        </div>
      )}
    </div>
  );
};
